import { api, ApiError } from "../core/api.js";
import { h, mount } from "../core/dom.js";
import { navigate } from "../core/router.js";
import { saveSession } from "../core/tokens.js";
import { OAUTH, OAUTH_REDIRECT_URI, POLICY_VERSION } from "../core/config.js";
import { t } from "../core/i18n.js";
import type { Session } from "../models.js";

/**
 * Web OAuth — Authorization Code + PKCE (01 §4.3, 04-web §2.2).
 * Self-issued JWT is SSOT; external IdP excluded. Native /token endpoint is for 02/03 only.
 * Flow: start() generates verifier/challenge, redirects to provider authorize URL →
 *       provider redirects back to /auth/callback?code&state →
 *       callbackView() POSTs code+verifier to the API, saves session, navigates /feed.
 */

const VERIFIER_KEY = "shortdrama.pkce.verifier";
const STATE_KEY = "shortdrama.pkce.state";
const PROVIDER_KEY = "shortdrama.pkce.provider";

function base64UrlEncode(bytes: ArrayBuffer): string {
  let str = "";
  const view = new Uint8Array(bytes);
  for (const b of view) str += String.fromCharCode(b);
  return btoa(str).replace(/\+/g, "-").replace(/\//g, "_").replace(/=+$/, "");
}

function randomString(byteLength: number): string {
  const bytes = new Uint8Array(byteLength);
  crypto.getRandomValues(bytes);
  return base64UrlEncode(bytes.buffer);
}

async function sha256Challenge(verifier: string): Promise<string> {
  const digest = await crypto.subtle.digest("SHA-256", new TextEncoder().encode(verifier));
  return base64UrlEncode(digest);
}

/** Begin the PKCE flow for a provider ('google' | 'apple'). Tolerant of missing client id. */
export async function start(provider: string): Promise<void> {
  const cfg = OAUTH[provider];
  if (!cfg || !cfg.clientId) {
    alert(
      `${provider} 로그인이 아직 설정되지 않았습니다.\n` +
        `index.html에 <meta name="${provider}-client-id" content="..."> 를 추가하세요.`,
    );
    return;
  }

  const verifier = randomString(48);
  const state = randomString(16);
  const challenge = await sha256Challenge(verifier);

  sessionStorage.setItem(VERIFIER_KEY, verifier);
  sessionStorage.setItem(STATE_KEY, state);
  sessionStorage.setItem(PROVIDER_KEY, provider);

  const params = new URLSearchParams({
    response_type: "code",
    client_id: cfg.clientId,
    redirect_uri: OAUTH_REDIRECT_URI,
    scope: cfg.scope,
    state,
    code_challenge: challenge,
    code_challenge_method: "S256",
  });
  if (provider === "apple") params.set("response_mode", "query");

  location.assign(`${cfg.authorizeUrl}?${params.toString()}`);
}

interface CallbackResponse extends Session {
  reconsent_required?: boolean;
}

/** Route /auth/callback — exchange the authorization code for a session. */
export async function callbackView(_params: Record<string, string>, root: HTMLElement): Promise<void> {
  mount(root, h("div", { class: "center muted" }, t("callback.processing")));

  const query = new URLSearchParams(location.search);
  const code = query.get("code");
  const state = query.get("state");
  const error = query.get("error");

  const provider = sessionStorage.getItem(PROVIDER_KEY) ?? "google";
  const verifier = sessionStorage.getItem(VERIFIER_KEY);
  const expectedState = sessionStorage.getItem(STATE_KEY);

  // TEMP DEBUG: report what the provider returned (captured server-side in KV).
  void api
    .post("/v1/debug/clientlog", {
      where: "oauth.callback",
      search: location.search,
      origin: location.origin,
      redirect_uri: OAUTH_REDIRECT_URI,
      has_code: !!code,
      error: error ?? null,
      has_verifier: !!verifier,
      state_match: state === expectedState,
    })
    .catch(() => undefined);

  const fail = (msg: string): void => {
    mount(
      root,
      h(
        "section",
        { style: "max-width:420px;margin:0 auto" },
        h("h1", {}, t("callback.failedTitle")),
        h("p", { class: "error" }, msg),
        h("a", { class: "btn", href: "/login", "data-link": "" }, t("common.retry")),
      ),
    );
  };

  if (error) return fail(t("callback.providerError", { error }));
  if (!code) return fail(t("callback.noCode"));
  if (!verifier) return fail(t("callback.verifierExpired"));
  if (!state || state !== expectedState) return fail(t("callback.stateMismatch"));

  try {
    const session = await api.post<CallbackResponse>(`/v1/auth/oauth/${provider}/callback`, {
      code,
      code_verifier: verifier,
      redirect_uri: OAUTH_REDIRECT_URI,
      state,
      policy_version: POLICY_VERSION,
    });
    sessionStorage.removeItem(VERIFIER_KEY);
    sessionStorage.removeItem(STATE_KEY);
    sessionStorage.removeItem(PROVIDER_KEY);
    saveSession(session);
    // TEMP DEBUG: confirm client-side the session was received + persisted.
    void api
      .post("/v1/debug/clientlog", {
        where: "oauth.callback.result",
        ok: true,
        got_access: !!session?.access_token,
        got_refresh: !!session?.refresh_token,
        saved: !!localStorage.getItem("shortdrama.session"),
      })
      .catch(() => undefined);

    if (session.reconsent_required) {
      alert(t("callback.reconsent"));
    }
    navigate("/feed");
  } catch (e) {
    const msg = e instanceof ApiError ? `${e.status} ${e.code}` : String(e);
    // TEMP DEBUG: capture what the client actually saw on failure.
    void api
      .post("/v1/debug/clientlog", {
        where: "oauth.callback.result",
        ok: false,
        error_kind: e instanceof ApiError ? "ApiError" : "other",
        status: e instanceof ApiError ? e.status : null,
        code: e instanceof ApiError ? e.code : null,
        message: String(e),
      })
      .catch(() => undefined);
    fail(t("callback.exchangeFailed", { msg }));
  }
}
